import { createStore } from 'owa-satcheljs';
import type {
    CopilotChatDrawerStore,
    CopilotChatDrawerState,
} from './schema/copilotChatDrawerStoreSchema';
import { DEFAULT_COPILOT_CHAT_DRAWER_WIDTH } from '../utils/copilotChatDrawerConstants';
import { getSavedCopilotChatDrawerWidth } from '../utils/copilotChatDrawerLocalStorage';

const copilotChatDrawerStore: CopilotChatDrawerStore = {
    chatDrawerStateMap: new Map<Window, CopilotChatDrawerState>(),
    heartbeatWorkingSetSizeMb: undefined,
    heartbeatCommitSizeMb: undefined,
    isProcessShared: undefined,
};

export const getStore: () => CopilotChatDrawerStore = createStore<CopilotChatDrawerStore>(
    'CopilotChatDrawerStore',
    copilotChatDrawerStore
);

/**
 * Returns the state for the given window, or undefined if not initialized.
 * Note: Selectors should call this function, as this does NOT create state as
 *       selectors should not mutate state.
 */
export const getCopilotChatDrawerState = (
    targetWindow: Window
): CopilotChatDrawerState | undefined => {
    return getStore().chatDrawerStateMap.get(targetWindow);
};

/**
 * Cleanup function to remove window instance key from store,
 * called when a window unmounts.
 */
export const removeCopilotChatDrawerState = (targetWindow: Window): void => {
    getStore().chatDrawerStateMap.delete(targetWindow);
};

/**
 * Get the initial user preferred width for the chat drawer.
 * If popout, we simplify and always resort to default width.
 * Otherwise, we check local storage for a saved width.
 */
function getInitialUserPreferredWidth(isPopoutWindow: boolean): number {
    if (isPopoutWindow) {
        return DEFAULT_COPILOT_CHAT_DRAWER_WIDTH;
    }

    return getSavedCopilotChatDrawerWidth() ?? DEFAULT_COPILOT_CHAT_DRAWER_WIDTH;
}

/**
 * Default initial state for a new window instance.
 * Use this as a baseline template for mutators that create state.
 */
export const getInitialState = (isPopoutWindow: boolean = false): CopilotChatDrawerState => {
    return {
        isShown: false,
        userPreferredWidth: getInitialUserPreferredWidth(isPopoutWindow),
        appCanvasWidth: 0,
        showInReadingPane: false,
    };
};
